import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import api from "../lib/api";
import { formatDate } from "../lib/format";
import DeleteConfirmModal from "./DeleteConfirmModal";

const STATUSES = ["new","contacted","qualified","won","lost"];
const SOURCES = ["", "contact", "vehicle_enquiry", "ai_assistant", "test_drive", "sell_car"];

const AdminLeads = () => {
  const [params, setParams] = useSearchParams();
  const source = params.get("source") || "";
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = async () => {
    const q = new URLSearchParams();
    if (source) q.set("source", source);
    if (status) q.set("status", status);
    const r = await api.get(`/admin/leads?${q.toString()}`);
    setItems(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [source, status]);

  const setSource = (s) => {
    const next = new URLSearchParams(params);
    if (!s) next.delete("source"); else next.set("source", s);
    setParams(next);
  };
  const update = async (id, patch) => {
    try { const r = await api.patch(`/admin/leads/${id}`, patch); setItems(items.map((x) => x.id === id ? r.data : x)); toast.success("Updated"); }
    catch { toast.error("Failed"); }
  };
  const requestDelete = (it) => setPendingDelete({ id: it.id, label: `${it.name}${it.source ? " · " + it.source.replace(/_/g, " ") : ""}` });
  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await api.delete(`/admin/leads/${pendingDelete.id}`);
      toast.success("Lead deleted");
      setPendingDelete(null);
      await load();
    } catch (e) {
      toast.error("Delete failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-leads">
      <div>
        <div className="overline text-[#7A1519]">CRM</div>
        <h1 className="font-display text-3xl md:text-4xl tracking-tight font-semibold">Customer Enquiries</h1>
      </div>
      <div className="flex flex-wrap gap-4 items-center">
        <div className="flex items-center gap-2">
          <div className="overline text-[#242424]/60">Source</div>
          <select value={source} onChange={(e) => setSource(e.target.value)} className="bg-white border hairline px-3 py-1.5 rounded-sm text-sm" data-testid="lead-src">
            {SOURCES.map((s) => <option key={s || "all"} value={s}>{s ? s.replace(/_/g, " ") : "All"}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <div className="overline text-[#242424]/60">Status</div>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="bg-white border hairline px-3 py-1.5 rounded-sm text-sm" data-testid="lead-stat">
            <option value="">All</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div className="bg-white border hairline rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F6F3] border-b hairline text-left text-[#242424]/60">
            <tr>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Phone / Email</th>
              <th className="px-4 py-3 font-medium">Vehicle</th>
              <th className="px-4 py-3 font-medium">Source</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Date</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-[#242424]/60">No leads.</td></tr>}
            {items.map((it) => (
              <React.Fragment key={it.id}>
                <tr onClick={() => setExpanded(expanded === it.id ? null : it.id)} className="border-b hairline hover:bg-[#F7F6F3]/50 cursor-pointer" data-testid={`lead-row-${it.id}`}>
                  <td className="px-4 py-3 font-medium">{it.name}</td>
                  <td className="px-4 py-3 text-xs">{it.phone}<br/>{it.email}</td>
                  <td className="px-4 py-3 text-xs">{it.vehicle_summary || "—"}</td>
                  <td className="px-4 py-3 text-xs capitalize">{(it.source || "").replace(/_/g, " ")}</td>
                  <td className="px-4 py-3">
                    <select value={it.status} onChange={(e) => { e.stopPropagation(); update(it.id, { status: e.target.value }); }} onClick={(e) => e.stopPropagation()} className="bg-white border hairline px-2 py-1 rounded-sm text-xs">
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-xs text-[#242424]/60">{formatDate(it.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={(e) => { e.stopPropagation(); requestDelete(it); }} className="p-2 border hairline rounded-sm hover:bg-[#F7F6F3] text-[#7A1519]" title="Delete lead" data-testid={`lead-del-${it.id}`}><Trash2 size={14} /></button>
                  </td>
                </tr>
                {expanded === it.id && (
                  <tr className="bg-[#F7F6F3]/40">
                    <td colSpan={7} className="p-4">
                      {it.message && <div className="text-sm mb-3"><span className="overline text-[#242424]/60 mr-2">Message:</span>{it.message}</div>}
                      <label className="overline text-[#242424]/60 block mb-1">Internal notes</label>
                      <textarea rows={3} defaultValue={it.internal_notes} onBlur={(e) => e.target.value !== it.internal_notes && update(it.id, { internal_notes: e.target.value })} className="w-full bg-white border hairline rounded-sm p-3 text-sm" data-testid={`lead-notes-${it.id}`} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <DeleteConfirmModal
        open={!!pendingDelete}
        recordType="lead"
        recordLabel={pendingDelete?.label || ""}
        onCancel={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default AdminLeads;
